import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import { 
  Star, 
  TrendingUp, 
  MessageSquare, 
  AlertCircle,
  ArrowRight,
  QrCode,
  Crown,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { organization, subscription, isPremium } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const recentReviews = reviews.slice(0, 5);
  const positiveReviews = reviews.filter(r => r.rating >= 4).length;
  const negativeReviews = reviews.filter(r => r.rating < 4).length;
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  const stats = [
    {
      label: "Note Google",
      value: organization?.google_rating?.toFixed(1) || "4.2",
      icon: Star,
      color: "text-amber-500",
      bgColor: "bg-amber-50"
    },
    {
      label: "Avis collectés",
      value: reviews.length,
      icon: MessageSquare,
      color: "text-violet-500",
      bgColor: "bg-violet-50"
    },
    {
      label: "Avis positifs",
      value: positiveReviews,
      icon: TrendingUp,
      color: "text-emerald-500",
      bgColor: "bg-emerald-50"
    },
    {
      label: "À traiter",
      value: negativeReviews,
      icon: AlertCircle,
      color: "text-orange-500",
      bgColor: "bg-orange-50"
    }
  ];

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "text-amber-400 fill-amber-400" : "text-slate-200"
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Tableau de bord
          </h1>
          <p className="text-slate-500 mt-1">
            Bienvenue sur ResponsIA, {organization?.name}
          </p>
        </div>
        <Link to="/qrcode">
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="view-qrcode-button">
            <QrCode className="w-4 h-4 mr-2" />
            Voir mon QR Code
          </Button>
        </Link>
      </div>


      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={index} 
            className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:shadow-slate-100 transition-all"
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center mb-4`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
            <p className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {stat.value}
            </p>
            <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Reviews */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-900">Derniers avis</h2>
            <Link to="/reviews" className="text-sm text-violet-600 hover:text-violet-700 font-medium">
              Voir tout
            </Link>
          </div>
          
          {recentReviews.length > 0 ? (
            <div className="space-y-4">
              {recentReviews.map((review) => (
                <div 
                  key={review.id} 
                  className={`p-4 rounded-xl border ${
                    review.rating >= 4 
                      ? "border-emerald-100 bg-emerald-50/50" 
                      : "border-orange-100 bg-orange-50/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">{review.author_name}</span>
                        {renderStars(review.rating)}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{review.comment}</p>
                    </div>
                    <div className="flex-shrink-0">
                      {review.is_published ? (
                        <span className="inline-flex items-center px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                          Publié
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                          Privé
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun avis pour le moment</p>
              <p className="text-sm text-slate-400 mt-1">
                Partagez votre QR Code pour collecter des avis
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Average Rating */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="text-sm font-medium text-slate-500 mb-4">Note moyenne</h3>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {avgRating}
              </div>
              <div>
                {renderStars(Math.round(parseFloat(avgRating)))}
                <p className="text-xs text-slate-400 mt-1">
                  sur {reviews.length} avis
                </p>
              </div>
            </div>
          </div>


          {/* Quick Tips */}
          <div className="bg-violet-50 rounded-2xl p-6 border border-violet-100">
            <h3 className="font-medium text-violet-900 mb-3">Conseil du jour</h3>
            <p className="text-sm text-violet-700 leading-relaxed">
              Placez votre QR Code près de la caisse ou sur vos supports de communication pour maximiser la collecte d'avis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
